import { NAME_TOOLTIP_TEXT, useAppForm } from '@massilia/features'
import { useInitiateLegacyDepositMutation } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Euro, Loader2, Send } from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod'

import { resolveAuthErrorMessage } from '@/lib/auth-error-message'
import { legacyDepositSchema } from '@/lib/schemas'

const legacySearchSchema = z.object({
  email: z.string().optional(),
  lastName: z.string().optional(),
  dossierNumber: z.string().optional(),
})

export const Route = createFileRoute('/_blue_header/payer-acompte/ancien')({
  component: LegacyDepositPage,
  validateSearch: legacySearchSchema,
})

function LegacyDepositPage() {
  const searchParams = Route.useSearch()
  const [submitError, setSubmitError] = useState<null | string>(null)
  const { mutateAsync: initiateLegacyDeposit } = useInitiateLegacyDepositMutation()

  const form = useAppForm({
    defaultValues: {
      email: searchParams.email ?? '',
      firstName: '',
      lastName: searchParams.lastName ?? '',
      amountEuros: 0,
      comment: '',
      dossierNumber: searchParams.dossierNumber ?? '',
      phone: '',
    },
    onSubmit: async ({ value }) => {
      try {
        const amountCents = Math.round(Number(value.amountEuros) * 100)

        const result = await initiateLegacyDeposit({
          email: value.email,
          firstName: value.firstName,
          lastName: value.lastName,
          amountCents,
          comment: value.comment || undefined,
          dossierNumber: value.dossierNumber,
          phone: value.phone,
        })

        window.location.assign(result.axeptaRedirectUrl)
      } catch (error) {
        setSubmitError(resolveAuthErrorMessage(error))
      }
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'blur',
    }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate({ validationSource: 'form', value }, legacyDepositSchema),
    },
  })

  return (
    <div className="mx-auto w-full max-w-lg">
      <div className="mb-6 text-center">
        <h1 className="text-gray-light-900 text-2xl font-semibold">
          Paiement pour les anciens clients
        </h1>
        <p className="text-gray-light-600 mt-2 text-sm">
          Si votre dossier a été créé avant le lancement du nouveau site, remplissez ce formulaire
          pour effectuer votre paiement.
        </p>
      </div>

      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          void form.handleSubmit()
        }}
      >
        <form.AppField
          name="lastName"
          children={(field) => <field.TextField label="Nom" questionToolTip={NAME_TOOLTIP_TEXT} />}
        />
        <form.AppField
          name="firstName"
          children={(field) => (
            <field.TextField label="Prénom" questionToolTip={NAME_TOOLTIP_TEXT} />
          )}
        />
        <form.AppField
          name="phone"
          children={(field) => <field.PhoneNumberField label="Téléphone" />}
        />
        <form.AppField
          name="email"
          children={(field) => <field.TextField label="Adresse email" />}
        />
        <form.AppField
          name="dossierNumber"
          children={(field) => <field.TextField label="Numéro de dossier" />}
        />
        <form.AppField
          name="comment"
          children={(field) => <field.TextAreaField label="Commentaire (optionnel)" />}
        />
        <form.AppField
          name="amountEuros"
          children={(field) => (
            <field.NumberField icon={Euro} decimal label="Montant à régler (en euros)" />
          )}
        />

        {submitError && <p className="text-sm font-medium text-red-600">{submitError}</p>}

        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <Button
              className="bg-brand-600 hover:bg-brand-700 mt-2 w-full px-4 py-3 text-base font-medium text-white"
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting ? (
                <Loader2 className="size-5 animate-spin" />
              ) : (
                <>
                  <Send className="mr-2 size-4" />
                  Procéder au paiement
                </>
              )}
            </Button>
          )}
        </form.Subscribe>

        <Button asChild className="w-full" type="button" variant="outline">
          <Link to="/payer-acompte">Retour</Link>
        </Button>
      </form>
    </div>
  )
}
